import { useContext, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { reportProblem } from '../api/feedback';
import { ApiError } from '../api/client';
import { SessionContext } from '../state/session';
import { Button } from '../ui';

/**
 * "Report a problem" — a link that opens a short form, anywhere something can go wrong.
 *
 * The owner, 2026-10-01: "lets make it easy to report a problem if there is one." The only door
 * was a mailto to support@saddle-sites.com, a domain with no MX record: every one bounced. The
 * report goes to the server, which keeps it and emails the owner; the page it was sent from, the
 * project and the last error go with it, so the person only has to say what went wrong.
 *
 * Somebody who is not signed in (stuck at sign-up, waiting for a code) can report too, and is
 * asked where to answer them — optional, never required.
 */
export default function ReportProblem({
  projectId,
  appId,
  lastError,
  label = 'Report a problem',
  className = 'link-button type-caption',
}: {
  projectId?: string;
  appId?: string;
  lastError?: string | null;
  label?: string;
  className?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();
  // The context itself, not useSession(): a report link must never crash the screen it is on,
  // and outside a provider (a test, an error screen) it simply means "not signed in".
  const user = useContext(SessionContext)?.user ?? null;
  const id = useId();
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [problem, setProblem] = useState<string | null>(null);
  /*
   * The form exists only while open: closed, its "Your email" field sat in every page that has a
   * report link — sign-up included, where it was a second email field for the browser and for
   * anything reading the page.
   */
  const [isOpen, setIsOpen] = useState(false);

  function open(): void {
    setState('idle');
    setProblem(null);
    setIsOpen(true);
    // After the content renders, so focus lands inside a dialog that has it.
    queueMicrotask(() => {
      dialog.current?.showModal?.();
      if (dialog.current && !dialog.current.open) dialog.current.setAttribute('open', '');
    });
  }
  function close(): void {
    if (dialog.current?.close) dialog.current.close();
    else dialog.current?.removeAttribute('open');
    setIsOpen(false);
  }

  async function send(): Promise<void> {
    const words = message.trim();
    if (!words || state === 'sending') return;
    setState('sending');
    setProblem(null);
    try {
      await reportProblem({
        message: words,
        page: pathname,
        ...(projectId ? { projectId } : {}),
        ...(appId ? { appId } : {}),
        ...(lastError ? { lastError: lastError.slice(0, 500) } : {}),
        ...(!user && contact.trim() ? { contact: contact.trim() } : {}),
      });
      setState('sent');
      setMessage('');
    } catch (err) {
      setState('idle');
      setProblem(err instanceof ApiError && err.message ? err.message : "That didn't send. Check your connection and try again.");
    }
  }

  return (
    <>
      <button type="button" className={className} onClick={open}>
        {label}
      </button>
      <dialog ref={dialog} className="confirm-dialog report-dialog" aria-labelledby={`${id}-title`} onClose={() => setIsOpen(false)}>
        {isOpen && (<>
        <h2 id={`${id}-title`} className="type-title">Report a problem</h2>
        {state === 'sent' ? (
          <>
            <p role="status" className="type-body">Thanks — we have it, with the page you were on. We'll look into it.</p>
            <div className="build-actions">
              <Button title="Close" onPress={close} />
            </div>
          </>
        ) : (
          <>
            <label className="type-caption field-label" htmlFor={`${id}-message`}>What went wrong?</label>
            <textarea
              id={`${id}-message`}
              className="field"
              rows={4}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="For example: my photos don't show on my site."
            />
            {!user && (
              <>
                <label className="type-caption field-label" htmlFor={`${id}-contact`}>Your email, if you'd like an answer (optional)</label>
                <input
                  id={`${id}-contact`}
                  className="field"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                />
              </>
            )}
            {problem && <p role="alert" className="type-caption field-error-text">{problem}</p>}
            <div className="build-actions">
              <Button title="Send report" onPress={() => void send()} loading={state === 'sending'} disabled={!message.trim()} />
              <Button title="Cancel" variant="ghost" onPress={close} />
            </div>
          </>
        )}
        </>)}
      </dialog>
    </>
  );
}
